// © Microsoft Corporation. All rights reserved.

import "../publicPath";

import { CommonHeader, CommonHeaderConfig, CommonHeaderConnector, CommonHeaderReducer, NavigationItem, SecondaryNavigationInfo, SportsEntryType } from "@msnews/common-header";
import { ExperienceIds } from "@msnews/experiences-constants";
import { RequestContext, resolver, DynamicBundles } from "@msnews/core";
import { handleAppErrorEventCall } from "../integration/PrimeTelemetry";

import { PageBase } from "@msnews/experiences-page-base";
import React from "react";
import { getTrack } from "../integration/PrimeTrack";

// register potential dynamic children of the river.
DynamicBundles.registerExperience(ExperienceIds.meControl, () => import(/* webpackChunkName: "me-control" */ "@msnews/me-control"));

(async () => {
    // Find the host element on the page
    const hostElement = document.getElementById("common-header-experience");
    // Get an instance of the PageBase for this app
    const app = PageBase.getInstance();

    const requestContext = new RequestContext();
    const config = await resolver.getConfig("cms/api/amp/experienceConfigIndex/BB17o60N");
    const configProps = {
        includePCSOnlyFlag: true,
        ...config.properties,
        disableFirstRow: true,
        forwardOcidOnPrimeExternal: true
    } as CommonHeaderConfig;

    const connector = new CommonHeaderConnector("CommonHeader", "", app.rootReducer, new CommonHeaderReducer(), app.store, configProps, requestContext);
    const rootUrl = hostElement.getAttribute("data-root-url");
    const verticalKey = hostElement.getAttribute("data-vertical-key");
    const categoryUrlSegment = hostElement.getAttribute("data-category-url-segment");
    const subCategoryUrlSegment = hostElement.getAttribute("data-subcategory-url-segment");
    const subSubcategoryUrlSegment = hostElement.getAttribute("data-subsubcategory-url-segment");
    const isContextualSecondaryNavEnabled = hostElement.getAttribute("data-is-secondary-nav-enabled") === "1";
    const isWindowsShellExperience = hostElement.getAttribute("data-is-windows-shell-experience") === "1";
    const isL3HeaderPresentInContent = hostElement.getAttribute("data-is-l3-present-in-content") === "1";
    const hideCommonHeaderPersonalizeButton = hostElement.getAttribute("data-hide-commonheader-personalize-button") === "1";

    configProps.isWindowsShellExperience = isWindowsShellExperience;
    configProps.primeOverrideVerticalKey = verticalKey;
    configProps.hidePersonalizeButton = hideCommonHeaderPersonalizeButton;

    connector.setOverrideRootUrl(rootUrl);
    connector.setVerticalKeyOverride(verticalKey);
    connector.setIsContextualSecondaryNavEnabled(isContextualSecondaryNavEnabled);

    let primePath = `/${verticalKey}`;
    if (categoryUrlSegment) {
        primePath += `/${categoryUrlSegment.toLowerCase()}`;
    }

    if (subCategoryUrlSegment) {
        primePath += `/${subCategoryUrlSegment.toLowerCase()}`;
    }

    if (subSubcategoryUrlSegment) {
        primePath += `/${subSubcategoryUrlSegment.toLowerCase()}`;
    }

    if (isContextualSecondaryNavEnabled) {
        connector.setPrimeSecondaryNavigationInfo(getRouteByPrimePathOrVerticalKeyWithSecondaryKey(verticalKey, configProps, primePath, isL3HeaderPresentInContent));
    }

    connector.setPrimePathOverride(primePath);

    // Setup design system
    const disableDarkmode = hostElement.getAttribute("data-disable-darkmode") === "True";

    // Create prime hybrid tracking functions
    const track = await getTrack();
    const trackEventCb = (element?, action?, behavior?, event?, destination?, headline?) => {
        track.trackEvent(event, element, destination, headline);
    };

    connector.setTrackingFunctions(trackEventCb, handleAppErrorEventCall);

    const CommonHeaderExperience = await connector.connectComponent(CommonHeader, {});

    app.renderRootElement(
        (<CommonHeaderExperience
            disableDarkmode={disableDarkmode}
            includeWrapperDesignSystemProvider={true}
            isContextualSecondaryNavEnabled={isContextualSecondaryNavEnabled}
            isWindowsShellExperience={isWindowsShellExperience}
            hideSecondaryNavAsL3InContent={isL3HeaderPresentInContent}
            primeOverrideVerticalKey={verticalKey}/>),
        hostElement);
})();

/**
     * Returns true if the portion of primePath's urlSegment that determines navigation has children
     * Eg: /sports/soccer/la-liga - La-liga has children. hence this will return true
     * Eg: /sports/my-favorites - my favorites does not have children -> return false
     * Eg: /sports/nfl/teama-vs-teamb/game-center - here only the /sports/nfl determines navigation and nfl has children. returns true
     * @param pathToIdMap Mapping between url Segment to the navigation Id
     * @param config CommonHeaderConfig that cotains the navigation Config
 */
export function doesLastNavigationItemHaveChildren(pathToIdMap: Map<string, string>, config: CommonHeaderConfig, primePathOverride: string): boolean | null {
    const { navigationConfig } = config;
    if (!primePathOverride) {
        return null;
    }
    let primePath = primePathOverride.substring(1); // remove the first "/"
    if (!primePath) {
        return null;
    }
    let indexOfsymbol = primePath.indexOf("/");
    let lastNavItemChildren = navigationConfig.contextualNavItems; // initialize to all the nav items
    let urlSegmentPath = "";
    while (primePath != null) {
        urlSegmentPath = indexOfsymbol != -1 ? urlSegmentPath + "/" + primePath.substring(0, indexOfsymbol) : urlSegmentPath + "/" + primePath;
        if (urlSegmentPath != null && pathToIdMap.has(urlSegmentPath)) {
            let navChildrenToNavigate: NavigationItem[] = null;
            if (lastNavItemChildren == null) {
                // Ideally this should not happen, but could because of incorrect config
                // Returning null to fall back to L1 nav only
                return null;
            }
            lastNavItemChildren.forEach(item => {
                if (item.id == pathToIdMap.get(urlSegmentPath)) {
                    navChildrenToNavigate = item.children;
                }
            });

            lastNavItemChildren = navChildrenToNavigate;
            primePath = indexOfsymbol != -1 && indexOfsymbol < primePath.length ? primePath.substring(indexOfsymbol + 1) : null;
            indexOfsymbol = primePath != null ? primePath.indexOf("/") : -1;
        } else {
            indexOfsymbol = -1;
            primePath = null;
        }
    }

    return (lastNavItemChildren != null && lastNavItemChildren.length > 0);
}

/**
 *
 * @param verticalKeyOverride The vertical key
 * @param config the CommonHeaderProps
 * @param primePathOverride PrimePath built from the details passed in
 * @returns SecondaryNavigationInfo interface type with details about primary and secondary nav info
 */
export function getRouteByPrimePathOrVerticalKeyWithSecondaryKey(verticalKeyOverride: string, config: CommonHeaderConfig, primePathOverride: string, isL3HeaderPresentInContent: boolean): SecondaryNavigationInfo {
    let selectedNavMenuId: string = null;
    let selectedNavItemId: string = null;
    let selectedSecondaryNavMenuId: string = null;
    let selectedSecondaryNavItemId: string = null;

    let selectedNavigationLevel3Id = null;
    let selectedNavigationLevel2Id = null;
    let selectedNavigationLevel1Id = null;

    if (verticalKeyOverride && config.verticalToNavItemIdMap[verticalKeyOverride] !== undefined) {
        if (primePathOverride) {
            let indexOfPathComponent = 0;
            let primePath = primePathOverride;
            if (primePath == "/" + verticalKeyOverride) {
                return {selectedNavMenuId: verticalKeyOverride, selectedNavItemId: verticalKeyOverride, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
            }

            const pathToIdMap: Map<string, string> = new Map<string, string>();
            while (indexOfPathComponent != -1) {
                if (primePath == "/" + verticalKeyOverride) {
                    if (pathToIdMap.has(primePath) == false) {
                        pathToIdMap.set(primePath, verticalKeyOverride);
                    }
                    if (selectedNavigationLevel3Id == null) {
                        selectedNavigationLevel3Id = verticalKeyOverride;
                    } else if (selectedNavigationLevel2Id == null) {
                        selectedNavigationLevel2Id = verticalKeyOverride;
                    } else if (selectedNavigationLevel1Id == null) {
                        selectedNavigationLevel1Id = verticalKeyOverride;
                    }
                } else if (primePath && config.primePathToNavItemIdMap[primePath]) {
                    if (pathToIdMap.has(primePath) == false) {
                        pathToIdMap.set(primePath, config.primePathToNavItemIdMap[primePath]);
                    }
                    if (selectedNavigationLevel3Id == null) {
                        selectedNavigationLevel3Id = config.primePathToNavItemIdMap[primePath];
                    } else if (selectedNavigationLevel2Id == null) {
                        selectedNavigationLevel2Id = config.primePathToNavItemIdMap[primePath];
                    } else if (selectedNavigationLevel1Id == null) {
                        selectedNavigationLevel1Id = config.primePathToNavItemIdMap[primePath];
                    }
                }

                indexOfPathComponent = primePath.lastIndexOf("/");
                primePath = primePath.substring(0, indexOfPathComponent);
            }

            selectedSecondaryNavItemId = selectedNavigationLevel3Id;
            selectedSecondaryNavMenuId = selectedNavigationLevel2Id;
            selectedNavItemId = selectedNavigationLevel2Id;
            selectedNavMenuId = selectedNavigationLevel1Id;
            if (selectedNavigationLevel3Id == verticalKeyOverride && selectedNavigationLevel2Id == null && selectedNavigationLevel1Id == null) {
                return {selectedNavMenuId: verticalKeyOverride, selectedNavItemId: verticalKeyOverride, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
            }

            if (selectedNavigationLevel1Id == null) {
                selectedSecondaryNavMenuId = selectedNavigationLevel3Id;
                selectedNavItemId = selectedNavigationLevel3Id;
                selectedNavMenuId = selectedNavigationLevel2Id;
            }

            if (selectedNavMenuId != null && selectedNavMenuId != verticalKeyOverride) {
                // We always want the L1 header nav menu id to match vertical
                selectedNavItemId = selectedNavMenuId;
                selectedNavMenuId = verticalKeyOverride;
            }

            const doLastNavigationItemHaveChildren = doesLastNavigationItemHaveChildren(pathToIdMap, config, primePathOverride);
            if (doLastNavigationItemHaveChildren == null || (selectedNavigationLevel1Id == null && doLastNavigationItemHaveChildren === false)) {
                selectedSecondaryNavMenuId = null;
                selectedSecondaryNavItemId = null;
            } else if ((doLastNavigationItemHaveChildren ?? false) == true) {
                selectedSecondaryNavMenuId = selectedSecondaryNavItemId;
            }

            if (selectedNavigationLevel2Id == null) {
                return {selectedNavMenuId: null, selectedNavItemId: null, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
            }

            const [finalSecondaryNavItemId, finalSecondaryNavMenuId] = getUpdatedNavItems(selectedNavItemId, selectedSecondaryNavMenuId, selectedSecondaryNavItemId, config, isL3HeaderPresentInContent);
            selectedSecondaryNavItemId = finalSecondaryNavItemId;
            selectedSecondaryNavMenuId = finalSecondaryNavMenuId;

            return {selectedNavMenuId, selectedNavItemId, selectedSecondaryNavMenuId, selectedSecondaryNavItemId};
        }

        return {selectedNavMenuId: null, selectedNavItemId: null, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
    }

    return {selectedNavMenuId: null, selectedNavItemId: null, selectedSecondaryNavMenuId: null, selectedSecondaryNavItemId: null};
}

export function getUpdatedNavItems(selectedNavItemId: string, selectedSecondaryNavMenuId: string, selectedSecondaryNavItemId: string, config: CommonHeaderConfig, isL3HeaderPresentInContent: boolean): [selectedSecondaryNavItemId: string, selectedSecondaryNavMenuId: string] {
    if (isL3HeaderPresentInContent) {
        let finalSecondaryNavItemId = "";
        let finalSecondaryNavMenuId = "";

        // step 1:  Overriding the secondary nav item id with secondary nav menu item id
        // step 2:  Overriding the secondary nav menu id with primary nav item id
        // step 3:  Set hideSecondaryNavAsL3InContent to true if the secondary nav item item id = nav menu id & the secondarynav menu id is not a sport.
        finalSecondaryNavItemId = selectedSecondaryNavMenuId;
        finalSecondaryNavMenuId = selectedNavItemId;

        config.hideSecondaryNavAsL3InContent = (finalSecondaryNavMenuId == finalSecondaryNavItemId) && !isSportsNavItem(finalSecondaryNavMenuId, config);
        return [finalSecondaryNavItemId, finalSecondaryNavMenuId];
    }

    return [selectedSecondaryNavItemId, selectedSecondaryNavMenuId];
}

// Will always return false in case of any error scenario
export function isSportsNavItem(navId: string, config: CommonHeaderConfig): boolean {
    const { navigationConfig } = config;
    const navConfig = navigationConfig.contextualNavItems;
    let sportsItem: NavigationItem;
    for (let i = 0; i < navConfig.length; i++) {
        if (navConfig[i] == null || navConfig[i].id != "sports") {
            continue;
        }

        sportsItem = navConfig[i];
        break;
    }

    if (sportsItem.children == null || sportsItem.children.length == 0) {
        return false;
    }

    for (let i = 0; i < sportsItem.children.length; i++) {
        if (sportsItem.children[i] == null || sportsItem.children[i].id == null) {
            continue;
        }

        if (sportsItem.children[i].id == navId) {
            const sportsEntryType = sportsItem.children[i].segmentSpecificMetadata?.sportsMetadata?.entryType;
            if (sportsEntryType == null) {
                return false;
            }

            return sportsEntryType == SportsEntryType.Sport;
        }
    }

    return false;
}